import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { flip, wobble, shine, poof } from '../styles/animations';

const TileContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'revealed',
      'revealedBy',
      'selected',
      'animation',
      'isFound',
      'isSwapping',
      'animationDelay',
      'playerId', // Forward playerId to use in styling
      'isProcessingMove', // Forward for styling/interaction
    ].includes(prop),
})`
  width: 39px;
  height: 39px;
  background-color: ${({ theme }) => theme.tileDefault};
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ isProcessingMove }) => (isProcessingMove ? 'wait' : 'pointer')};
  // border: 1px solid ${({ theme }) => theme.tileDefaultBorder};
  transition: all 0.3s ease;
  animation: ${({ revealed }) =>
    revealed ? css`${flip} 0.6s cubic-bezier(0.455, 0.030, 0.515, 0.955) both` : 'none'};
  position: relative;
  overflow: hidden;

  // No background color changes - circle handles all reveal states

  ${({ selected, theme }) =>
    selected &&
    css`
      box-shadow: 0 0 0 3px ${theme.tileSelectedBorder || '#8774de'} inset;
    `}

  ${({ animation }) =>
    animation &&
    css`
      animation: ${animation.type} ${animation.duration}s forwards;
    `}
  
  ${({ isFound, animationDelay, theme }) =>
    isFound &&
    css`
      animation: ${poof} 1s ease;
      animation-delay: ${animationDelay || 0}s;
      animation-fill-mode: both;
      background-color: ${theme.tileFound} !important;
      color: ${theme.tileText};
      border-color: ${theme.tileFoundBorder} !important;

      &::after {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background-image: linear-gradient(
          120deg,
          rgba(255, 255, 255, 0) 20%,
          rgba(255, 255, 255, 0.95) 50%,
          rgba(255, 255, 255, 0) 80%
        );
        animation: ${shine} 1.5s infinite;
        animation-delay: ${animationDelay || 0}s;
      }
    `}
  
  ${({ isSwapping }) =>
    isSwapping &&
    css`
      animation: ${wobble} 1s ease;
    `}
`;

const Letter = styled.span.withConfig({
  shouldForwardProp: (prop) => !['revealed'].includes(prop),
})`
  font-size: 16px;
  color: ${({ theme }) => theme.tileText};
  visibility: ${({ revealed }) => (revealed ? 'visible' : 'hidden')};
  z-index: 2;
  position: relative;
`;

const RevealCircle = styled.div.withConfig({
  shouldForwardProp: (prop) => !['circleColor'].includes(prop),
})`
  position: absolute;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: ${({ circleColor }) => circleColor || 'transparent'};
  // border: 1px solid ${({ circleColor }) => circleColor || 'transparent'};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const QuarterIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => !['indicatorColor', 'position', 'layout'].includes(prop),
})`
  position: absolute;
  background: ${({ indicatorColor }) => indicatorColor || 'transparent'};
  z-index: 0;
  opacity: 0.8;
  
  ${({ position, layout }) => {
    // Full tile (2 players or single-player AI)
    if (layout === 'full' || position === 'full') {
      return css`
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        border-radius: 5px;
      `;
    }
    
    // Half tile (3 players)
    if (layout === 'half') {
      switch(position) {
        case 'left':
          return css`
            top: 0;
            left: 0;
            bottom: 0;
            width: 50%;
            height: 100%;
            border-radius: 5px 0 0 5px;
          `;
        case 'right':
          return css`
            top: 0;
            right: 0;
            bottom: 0;
            width: 50%;
            height: 100%;
            border-radius: 0 5px 5px 0;
          `;
        default:
          return '';
      }
    }
    
    // Quarter tile (4-5 players) - default
    switch(position) {
      case 'top-right':
        return css`
          top: 0;
          right: 0;
          width: 50%;
          height: 50%;
          border-radius: 0 5px 0 0;
        `;
      case 'bottom-right':
        return css`
          bottom: 0;
          right: 0;
          width: 50%;
          height: 50%;
          border-radius: 0 0 5px 0;
        `;
      case 'bottom-left':
        return css`
          bottom: 0;
          left: 0;
          width: 50%;
          height: 50%;
          border-radius: 0 0 0 5px;
        `;
      case 'top-left':
        return css`
          top: 0;
          left: 0;
          width: 50%;
          height: 50%;
          border-radius: 5px 0 0 0;
        `;
      default:
        return '';
    }
  }}
`;

// Map a specific player ID to their consistent opponent color
// This ensures each player always gets the same color across all tiles
const getPlayerOpponentColor = (opponentPlayerId, currentPlayerId, theme) => {
  const colors = getOpponentColors(theme);
  
  // For AI in single player
  if (opponentPlayerId === 'ai') {
    return theme.tileAI || theme.tileOpponent || colors[0];
  }
  
  // Create a consistent ordered list of all possible players except current
  const allPossiblePlayers = ['player1', 'player2', 'player3', 'player4', 'player5'];
  const otherPlayers = allPossiblePlayers.filter(p => p !== currentPlayerId);
  
  // Find the index of this opponent in the ordered list
  const colorIndex = otherPlayers.indexOf(opponentPlayerId);
  
  return colors[colorIndex % colors.length];
};

// Player color mapping - Center circle ONLY for current player's reveals
const getPlayerColor = (revealedBy, playerId, theme) => {
  if (!revealedBy || revealedBy.length === 0) return null;
  
  const currentPlayerId = playerId || 'player';
  const isCurrentPlayerRevealed = revealedBy.includes(currentPlayerId);
  
  // Only show the main circle if the current player has revealed it
  if (isCurrentPlayerRevealed) {
    return theme.tilePlayer;
  }
  
  return null; // No circle if only opponents revealed it
};

// Get colors for OTHER players (not the current player)
// These are the 4 distinct colors for quarters from the theme
const getOpponentColors = (theme) => {
  return theme.opponentColors;
};

// Get indicators for OTHER players based on total player count
// Layout changes based on number of players:
// - 2 players: Full tile background
// - 3 players: Half tile (left/right)
// - 4-5 players: Quarter tiles
const getQuarterIndicators = (revealedBy, playerId, theme, totalPlayers = 2) => {
  if (!revealedBy || revealedBy.length === 0) return [];
  
  const currentPlayerId = playerId || 'player';
  
  // Get OTHER players who revealed it (excluding current player)
  const otherPlayers = revealedBy.filter(id => id !== currentPlayerId);
  
  if (otherPlayers.length === 0) {
    return []; // No other players revealed it
  }
  
  const indicators = [];
  
  // For single-player with AI - always full tile
  if (!playerId && otherPlayers.includes('ai')) {
    indicators.push({
      position: 'full',
      color: theme.tileAI || theme.tileOpponent,
      layout: 'full'
    });
    return indicators;
  }
  
  // For multiplayer: determine layout based on total players
  const sortedOtherPlayers = otherPlayers.sort();
  
  if (totalPlayers === 2) {
    // 2 players: show full tile for the one opponent
    sortedOtherPlayers.forEach((otherPlayerId) => {
      const color = getPlayerOpponentColor(otherPlayerId, currentPlayerId, theme);
      indicators.push({
        position: 'full',
        color: color,
        layout: 'full'
      });
    });
  } else if (totalPlayers === 3) {
    // 3 players: show half tiles (left and right halves)
    const halfPositions = ['left', 'right'];
    sortedOtherPlayers.forEach((otherPlayerId, index) => {
      const color = getPlayerOpponentColor(otherPlayerId, currentPlayerId, theme);
      const allPossibleOpponents = ['player1', 'player2', 'player3', 'player4', 'player5'].filter(p => p !== currentPlayerId).sort();
      const opponentIndex = allPossibleOpponents.indexOf(otherPlayerId);
      
      if (opponentIndex !== -1 && opponentIndex < halfPositions.length) {
        indicators.push({
          position: halfPositions[opponentIndex],
          color: color,
          layout: 'half'
        });
      }
    });
  } else {
    // 4-5 players: show quarter tiles (current behavior)
    const quarterPositions = ['top-right', 'bottom-right', 'bottom-left', 'top-left'];
    sortedOtherPlayers.forEach((otherPlayerId) => {
      const color = getPlayerOpponentColor(otherPlayerId, currentPlayerId, theme);
      const allPossibleOpponents = ['player1', 'player2', 'player3', 'player4', 'player5'].filter(p => p !== currentPlayerId).sort();
      const opponentIndex = allPossibleOpponents.indexOf(otherPlayerId);
      
      if (opponentIndex !== -1 && opponentIndex < quarterPositions.length) {
        indicators.push({
          position: quarterPositions[opponentIndex],
          color: color,
          layout: 'quarter'
        });
      }
    });
  }
  
  return indicators;
};

const Tile = ({ letter, revealedBy, onClick, selected, isFound, isSwapping, animationDelay, playerId, isProcessingMove, totalPlayers }) => {
  const theme = useTheme();
  
  // Is the tile revealed by anyone? This triggers the flip animation and circle display.
  const isRevealed = revealedBy && revealedBy.length > 0;

  // Should the letter be visible to the current player?
  // In single-player, playerId is null, so we check for the string 'player'.
  // In multiplayer, we check for the specific playerId.
  const showLetter = playerId ? revealedBy.includes(playerId) : revealedBy.includes('player');

  const handleClick = () => {
    if (isProcessingMove) return; // Prevent clicks while a move is processing
    onClick();
  };

  const circleColor = isRevealed ? getPlayerColor(revealedBy, playerId, theme) : null;
  const quarterIndicators = getQuarterIndicators(revealedBy, playerId, theme, totalPlayers);

  return (
    <TileContainer
      revealedBy={revealedBy}
      onClick={handleClick}
      selected={selected}
      isFound={isFound}
      isSwapping={isSwapping}
      animationDelay={animationDelay}
      playerId={playerId} // Pass playerId for styling
      revealed={isRevealed}
      isProcessingMove={isProcessingMove}
    >
      {/* Indicators for other players (layout varies by player count) */}
      {quarterIndicators.map((indicator, index) => (
        <QuarterIndicator
          key={`${indicator.position}-${index}`}
          position={indicator.position}
          indicatorColor={indicator.color}
          layout={indicator.layout}
        />
      ))}
      {/* Circle indicator only for current player's reveals */}
      {circleColor && <RevealCircle circleColor={circleColor} />}
      {/* The Letter's visibility is now correctly tied to only the player's own reveals */}
      <Letter revealed={showLetter}>{letter}</Letter>
    </TileContainer>
  );
};

export default Tile; 